import { useQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  User,
  GitBranch,
  Shield,
  XCircle,
  SkipForward,
  FileText,
  ThumbsUp,
  ThumbsDown,
  RefreshCw,
  Edit2,
} from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { PROC_AUDIT_ACTION_LABELS } from './auditActionLabels.ts'

// ── Types ────────────────────────────────────────────────────────────────────

interface InstanceStep {
  id: string
  template_step_id: string
  step_title: string | null
  step_description: string | null
  step_type: 'task' | 'approval' | 'form' | 'notification' | 'condition'
  sort_order: number
  is_required: number
  status: 'pending' | 'in_progress' | 'completed' | 'skipped' | 'rejected'
  assignee_user_id: string | null
  assignee_name: string | null
  note: string | null
  started_at: string | null
  completed_at: string | null
}

interface ProcessDetail {
  id: string
  title: string
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'rejected'
  template_id: string
  template_name: string | null
  template_description: string | null
  template_category: string | null
  started_by_user_id: string | null
  started_by_name: string | null
  due_date: string | null
  completed_at: string | null
  created_at: string
  steps: InstanceStep[]
}

interface AuditEntry {
  id: string
  instance_id: string
  instance_step_id: string | null
  actor_user_id: string | null
  actor_name: string | null
  step_title: string | null
  action: string
  details: Record<string, unknown> | null
  created_at: string
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const STEP_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  task: CheckCircle2,
  approval: Shield,
  form: FileText,
  notification: Clock,
  condition: GitBranch,
}

const STEP_STATUS_CONFIG: Record<string, { color: string; icon: React.ComponentType<{ className?: string }> }> = {
  pending: { color: 'text-neutral-400', icon: Clock },
  in_progress: { color: 'text-blue-500', icon: RefreshCw },
  completed: { color: 'text-green-500', icon: CheckCircle2 },
  rejected: { color: 'text-red-500', icon: XCircle },
  skipped: { color: 'text-neutral-400', icon: SkipForward },
}

const AUDIT_ACTION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  instance_created: GitBranch,
  instance_completed: CheckCircle2,
  instance_cancelled: XCircle,
  instance_rejected: XCircle,
  step_completed: CheckCircle2,
  step_rejected: XCircle,
  step_started: RefreshCw,
  step_skipped: SkipForward,
  approval_granted: ThumbsUp,
  approval_rejected: ThumbsDown,
  approval_delegated: User,
  form_submitted: FileText,
  assignment_changed: User,
  context_updated: Edit2,
  due_date_changed: Clock,
  note_added: FileText,
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return '–'
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StepList({ steps }: { steps: InstanceStep[] }) {
  if (steps.length === 0) {
    return (
      <EmptyState
        icon={<GitBranch className="w-6 h-6" />}
        title="Keine Schritte"
        description="Diese Prozessvorlage enthält keine Schritte."
      />
    )
  }
  return (
    <ol className="relative border-l border-neutral-200 ml-4 space-y-6">
      {steps.map((step, idx) => {
        const StepIcon = STEP_TYPE_ICONS[step.step_type] ?? CheckCircle2
        const statusCfg = STEP_STATUS_CONFIG[step.status] ?? STEP_STATUS_CONFIG['pending']!
        const StatusIcon = statusCfg.icon
        return (
          <li key={step.id} className="ml-6">
            <span className="absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full bg-white border border-neutral-200">
              <StatusIcon className={`w-3.5 h-3.5 ${statusCfg.color}`} />
            </span>
            <div className="bg-white border border-neutral-200 rounded-lg p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <StepIcon className="w-4 h-4 text-neutral-400 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-neutral-900 truncate">
                      {idx + 1}. {step.step_title ?? 'Schritt'}
                    </div>
                    {step.step_description && (
                      <div className="text-sm text-neutral-500 mt-0.5">{step.step_description}</div>
                    )}
                  </div>
                </div>
                <StatusBadge status={step.status} />
              </div>
              {(step.assignee_name || step.note || step.completed_at) && (
                <div className="mt-3 pt-3 border-t border-neutral-100 text-xs text-neutral-500 space-y-1">
                  {step.assignee_name && (
                    <div className="flex items-center gap-1.5">
                      <User className="w-3 h-3" />
                      {step.assignee_name}
                    </div>
                  )}
                  {step.completed_at && (
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3 h-3" />
                      Abgeschlossen: {formatDate(step.completed_at)}
                    </div>
                  )}
                  {step.note && (
                    <div className="italic">"{step.note}"</div>
                  )}
                </div>
              )}
            </div>
          </li>
        )
      })}
    </ol>
  )
}

function AuditLog({ instanceId }: { instanceId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['prozesse-audit', instanceId],
    queryFn: () =>
      apiClient
        .get<AuditEntry[]>(`/prozesse/instances/${instanceId}/audit-log`)
        .then((r) => r.data),
  })

  if (isLoading) return <LoadingState label="Audit-Log wird geladen…" />
  if (error) {
    return (
      <ErrorState
        title="Fehler beim Laden"
        description="Audit-Log konnte nicht geladen werden."
        details={error instanceof Error ? error.message : String(error)}
      />
    )
  }
  if (!data?.length) {
    return (
      <EmptyState
        icon={<Shield className="w-6 h-6" />}
        title="Keine Einträge"
        description="Noch keine Audit-Log-Einträge vorhanden."
      />
    )
  }

  return (
    <div className="space-y-1">
      {/* NIS2/KRITIS compliance header */}
      <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700 mb-4">
        <Shield className="w-4 h-4 shrink-0" />
        <span>NIS2/KRITIS-konformes Audit-Log — vollständige, unveränderliche Protokollierung aller Aktionen</span>
      </div>

      <ol className="relative border-l border-neutral-200 ml-4 space-y-4">
        {data.map((entry) => {
          const Icon = AUDIT_ACTION_ICONS[entry.action] ?? GitBranch
          const label = (PROC_AUDIT_ACTION_LABELS as Record<string, string>)[entry.action] ?? entry.action
          return (
            <li key={entry.id} className="ml-6">
              <span className="absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full bg-white border border-neutral-200">
                <Icon className="w-3.5 h-3.5 text-neutral-500" />
              </span>
              <div className="bg-white border border-neutral-200 rounded-lg px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <span className="font-medium text-neutral-800">{label}</span>
                    {entry.step_title && (
                      <span className="text-neutral-500"> · {entry.step_title}</span>
                    )}
                    {entry.actor_name && (
                      <div className="flex items-center gap-1 mt-0.5 text-xs text-neutral-500">
                        <User className="w-3 h-3" />
                        {entry.actor_name}
                      </div>
                    )}
                    {entry.details && Object.keys(entry.details).length > 0 && (
                      <details className="mt-1.5 text-xs text-neutral-500">
                        <summary className="cursor-pointer hover:text-neutral-700">Details anzeigen</summary>
                        <pre className="mt-1 p-2 bg-neutral-50 rounded text-xs overflow-auto max-h-32">
                          {JSON.stringify(entry.details, null, 2)}
                        </pre>
                      </details>
                    )}
                  </div>
                  <time className="text-xs text-neutral-400 whitespace-nowrap shrink-0">
                    {formatDate(entry.created_at)}
                  </time>
                </div>
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}

// ── Main component ───────────────────────────────────────────────────────────

export function ProcessDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data, isLoading, error } = useQuery({
    queryKey: ['prozesse-instance', id],
    queryFn: () =>
      apiClient
        .get<ProcessDetail>(`/prozesse/instances/${id}`)
        .then((r) => r.data),
    enabled: !!id,
  })

  if (isLoading) return <LoadingState label="Prozess wird geladen…" />
  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          title="Fehler beim Laden"
          description="Der Prozess konnte nicht geladen werden."
          details={error instanceof Error ? error.message : String(error)}
        />
      </div>
    )
  }
  if (!data) return null

  const completedSteps = data.steps.filter((s) => s.status === 'completed').length
  const totalSteps = data.steps.length
  const progress = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0
  const isOverdue = data.due_date && new Date(data.due_date) < new Date() && data.status !== 'completed' && data.status !== 'cancelled'

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={data.title}
        breadcrumbs={[
          { label: 'Prozesse', to: '/prozesse' },
          { label: data.title },
        ]}
        description={data.template_name ?? undefined}
        actions={
          <Button variant="secondary" onClick={() => navigate('/prozesse')}>
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Übersicht
          </Button>
        }
      />

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <Card.Body className="py-3">
            <div className="text-xs text-neutral-500 mb-1">Status</div>
            <StatusBadge status={data.status} />
          </Card.Body>
        </Card>
        <Card>
          <Card.Body className="py-3">
            <div className="text-xs text-neutral-500 mb-1">Fortschritt</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-neutral-200 rounded-full overflow-hidden">
                <div className="h-full bg-brand-500 rounded-full" style={{ width: `${progress}%` }} />
              </div>
              <span className="text-sm font-medium tabular-nums">{completedSteps}/{totalSteps}</span>
            </div>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body className="py-3">
            <div className="text-xs text-neutral-500 mb-1">Gestartet von</div>
            <div className="text-sm font-medium text-neutral-800">{data.started_by_name ?? '–'}</div>
          </Card.Body>
        </Card>
        <Card>
          <Card.Body className="py-3">
            <div className={`text-xs mb-1 ${isOverdue ? 'text-red-500' : 'text-neutral-500'}`}>
              {isOverdue ? 'Überfällig' : 'Fällig'}
            </div>
            <div className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-neutral-800'}`}>
              {data.due_date ? formatDate(data.due_date) : '–'}
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">
            <GitBranch className="w-4 h-4 mr-1.5" />
            Übersicht
          </TabsTrigger>
          <TabsTrigger value="audit-log">
            <Shield className="w-4 h-4 mr-1.5" />
            Audit-Log
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <Card>
            <Card.Header>
              <div className="font-semibold text-neutral-800">Prozessschritte</div>
              <div className="text-sm text-neutral-500">
                {completedSteps} von {totalSteps} Schritten abgeschlossen
              </div>
            </Card.Header>
            <Card.Body>
              <StepList steps={data.steps} />
            </Card.Body>
          </Card>

          {data.template_description && (
            <Card className="mt-4">
              <Card.Header>
                <div className="font-semibold text-neutral-800">Prozessbeschreibung</div>
              </Card.Header>
              <Card.Body>
                <p className="text-sm text-neutral-600">{data.template_description}</p>
              </Card.Body>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="audit-log" className="mt-4">
          <Card>
            <Card.Header>
              <div className="font-semibold text-neutral-800">Audit-Log</div>
              <div className="text-sm text-neutral-500">
                Vollständige Protokollierung aller Aktionen (NIS2/KRITIS-konform)
              </div>
            </Card.Header>
            <Card.Body>
              {id && <AuditLog instanceId={id} />}
            </Card.Body>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
